/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import BaseMapWrapper, { BaseMapWrapperProps } from './BaseMapWrapper';
import MapOverlays from './MapOverlays';
import googleMapTheme from '../theme/googleMapsTheme.json';
import googleMapThemeDetailed from '../theme/googleMapsThemeDetailed.json';
import { useSelector } from 'react-redux';
import { selectMapHidden } from '../../../store';

export const MapContext = React.createContext({} as google.maps.Map);

// location in the middle of the Atlantic Ocean
export const INITIAL_LATITUDE = 23.065;
export const INITIAL_LONGITUDE = -37.415;
export const INITIAL_ZOOM = 3;
const MINIMUM_ZOOM = 2;

interface BaseMapComponentProps extends BaseMapWrapperProps {
  mapLabelsEnabled: boolean;
  mapSatelliteViewEnabled: boolean;
  children: React.ReactNode;

  // MUST BE UNIQUE
  id: string;
}

/**
 * The BaseMapComponent is the container of the google maps api. This component should be rendered
 * at the top level of the application. It renders a map given a specific theme and
 * custom controls (i.e. zoom in, zoom out, full screen mode).
 */
function BaseMapComponent(props: BaseMapComponentProps) {
  const { mapLabelsEnabled, mapSatelliteViewEnabled, height, width, left, top, children, id } = props;

  const [map, setMap] = useState<null | google.maps.Map>(null);
  const [loaded, setLoaded] = useState(false);
  const isHidden = useSelector(selectMapHidden(id));

  const googleMapDiv = useCallback(
    (node: HTMLDivElement) => {
      if (node) {
        const mapTheme = googleMapTheme as google.maps.MapTypeStyle[];

        let map = new google.maps.Map(node, {
          center: { lat: INITIAL_LATITUDE, lng: INITIAL_LONGITUDE },
          zoom: INITIAL_ZOOM,
          minZoom: MINIMUM_ZOOM,
          disableDefaultUI: true, // turn off default controls to inject controls with custom styles
          styles: mapTheme,
          backgroundColor: '#3e4f5f',
        });

        // save google map object on the window object to help with testing
        window[id] = map;

        setMap(map);
        setLoaded(true);
      }
    },
    [id],
  );

  useEffect(() => {
    if (map) {
      if (mapLabelsEnabled) {
        map.setOptions({ styles: googleMapThemeDetailed as google.maps.MapTypeStyle[] });
      } else {
        map.setOptions({ styles: googleMapTheme as google.maps.MapTypeStyle[] });
      }
    }
  }, [mapLabelsEnabled, map]);

  useEffect(() => {
    if (map) {
      if (mapSatelliteViewEnabled) {
        map.setMapTypeId(getSatelliteMapTypeId());
      } else {
        map.setMapTypeId(getDefaultMapTypeId());
      }
    }
  }, [mapSatelliteViewEnabled, map]);

  return (
    <MapContext.Provider value={map as google.maps.Map}>
      <BaseMapWrapper
        id={id}
        height={height}
        width={width}
        left={left}
        top={top}
        ref={googleMapDiv}
        isHidden={isHidden}
      >
        {loaded ? children : null}
        {map ? <MapOverlays map={map} loaded={loaded} /> : <></>}
      </BaseMapWrapper>
    </MapContext.Provider>
  );
}

export default BaseMapComponent;

function getDefaultMapTypeId(): google.maps.MapTypeId {
  return google.maps.MapTypeId.ROADMAP;
}

function getSatelliteMapTypeId(): google.maps.MapTypeId {
  return google.maps.MapTypeId.HYBRID;
}
